import { validateArray, validateFitted } from '../utils/validation.js';

export class LabelEncoder {
  constructor() {
    this.classes = null;
    this.classToIndex = null;
    this.isFitted = false;
  }

  fit(y) {
    validateArray(y, 'y');

    this.classes = Array.from(new Set(y)).sort();
    this.classToIndex = {};

    this.classes.forEach((label, index) => {
      this.classToIndex[label] = index;
    });

    this.isFitted = true;
    return this;
  }

  transform(y) {
    validateFitted(this.isFitted, 'LabelEncoder');
    validateArray(y, 'y');

    return y.map(label => {
      if (!(label in this.classToIndex)) {
        throw new Error(`Label '${label}' not found in fitted classes`);
      }
      return this.classToIndex[label];
    });
  }

  fitTransform(y) {
    this.fit(y);
    return this.transform(y);
  }

  inverseTransform(y) {
    validateFitted(this.isFitted, 'LabelEncoder');
    validateArray(y, 'y');

    return y.map(index => {
      if (index < 0 || index >= this.classes.length) {
        throw new Error(`Index ${index} out of range for fitted classes`);
      }
      return this.classes[index];
    });
  }
}

export class OneHotEncoder {
  constructor(options = {}) {
    this.categories = null;
    this.dropFirst = options.dropFirst || false;
    this.isFitted = false;
  }

  fit(X) {
    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }

    const nFeatures = X[0].length;
    this.categories = Array(nFeatures);

    for (let j = 0; j < nFeatures; j++) {
      const column = X.map(row => row[j]);
      this.categories[j] = Array.from(new Set(column)).sort();
    }

    this.isFitted = true;
    return this;
  }

  transform(X) {
    validateFitted(this.isFitted, 'OneHotEncoder');

    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }

    return X.map(row => {
      const encoded = [];

      for (let j = 0; j < row.length; j++) {
        const value = row[j];
        const categories = this.categories[j];

        if (!categories.includes(value)) {
          throw new Error(`Value '${value}' not found in fitted categories for feature ${j}`);
        }

        const startIdx = this.dropFirst ? 1 : 0;
        for (let k = startIdx; k < categories.length; k++) {
          encoded.push(categories[k] === value ? 1 : 0);
        }
      }

      return encoded;
    });
  }

  fitTransform(X) {
    this.fit(X);
    return this.transform(X);
  }

  inverseTransform(X) {
    validateFitted(this.isFitted, 'OneHotEncoder');

    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }

    return X.map(row => {
      const decoded = [];
      let idx = 0;

      for (let j = 0; j < this.categories.length; j++) {
        const categories = this.categories[j];
        const nCategories = this.dropFirst ? categories.length - 1 : categories.length;

        const oneHot = row.slice(idx, idx + nCategories);
        idx += nCategories;

        let found = false;
        const startIdx = this.dropFirst ? 1 : 0;

        for (let k = 0; k < oneHot.length; k++) {
          if (oneHot[k] === 1) {
            decoded.push(categories[startIdx + k]);
            found = true;
            break;
          }
        }

        if (!found) {
          if (this.dropFirst) {
            decoded.push(categories[0]);
          } else {
            decoded.push(null);
          }
        }
      }

      return decoded;
    });
  }
}
